import type { ChatEvent } from "../api/sse";
import type { PersistedMessage } from "../api/client";

export type ChatMessageRole = "user" | "assistant";

export interface ToolEventGroup {
  id: string;
  title: string;
  kind?: string;
  status: "running" | "done" | "error";
  events: ChatEvent[];
}

export interface ChatMessage {
  id: string;
  role: ChatMessageRole;
  text: string;
  createdAt: string;
  thoughts: string[];
  toolEvents: ToolEventGroup[];
  isStreaming?: boolean;
}

export function messageFromPersisted(message: PersistedMessage): ChatMessage {
  const parsed = parsePersistedContent(message.content_json);
  return {
    id: message.id,
    role: message.role === "assistant" ? "assistant" : "user",
    text: parsed.text,
    createdAt: message.created_at,
    thoughts: [],
    toolEvents: parsed.toolEvents,
  };
}

export function extractMessageText(contentJson: string): string {
  return parsePersistedContent(contentJson).text;
}

function parsePersistedContent(contentJson: string): {
  text: string;
  toolEvents: ToolEventGroup[];
} {
  try {
    const parsed = JSON.parse(contentJson) as {
      text?: unknown;
      tool_events?: unknown;
    };
    const text = typeof parsed.text === "string" ? parsed.text : contentJson;
    const toolEvents = Array.isArray(parsed.tool_events)
      ? hydrateToolSummaries(parsed.tool_events)
      : [];
    return { text, toolEvents };
  } catch {
    return { text: contentJson, toolEvents: [] };
  }
}

function hydrateToolSummaries(raw: unknown[]): ToolEventGroup[] {
  const groups: ToolEventGroup[] = [];
  for (let i = 0; i < raw.length; i += 1) {
    const item = raw[i] as {
      id?: unknown;
      title?: unknown;
      kind?: unknown;
      status?: unknown;
    };
    const id = typeof item.id === "string" && item.id ? item.id : `tool-${i + 1}`;
    const title = typeof item.title === "string" && item.title ? item.title : "工具调用";
    const kind = typeof item.kind === "string" && item.kind ? item.kind : undefined;
    const status: ToolEventGroup["status"] =
      item.status === "error" ? "error" : item.status === "running" ? "running" : "done";
    groups.push({ id, title, kind, status, events: [] });
  }
  return groups;
}

export function addToolEvent(groups: ToolEventGroup[], event: ChatEvent): ToolEventGroup[] {
  const toolEvent = event as {
    tool_call_id?: string | null;
    title?: string | null;
    kind?: string;
    status?: string;
  };
  const id =
    typeof toolEvent.tool_call_id === "string" && toolEvent.tool_call_id
      ? toolEvent.tool_call_id
      : `tool-${groups.length + 1}`;

  const existingIndex = groups.findIndex((group) => group.id === id);
  const title =
    typeof toolEvent.title === "string" && toolEvent.title
      ? toolEvent.title
      : existingIndex >= 0
        ? groups[existingIndex].title
        : "工具调用";

  const nextGroup: ToolEventGroup = {
    id,
    title,
    kind: typeof toolEvent.kind === "string" ? toolEvent.kind : undefined,
    status:
      event.type === "tool_end"
        ? toolEvent.status === "error"
          ? "error"
          : "done"
        : "running",
    events: existingIndex >= 0 ? [...groups[existingIndex].events, event] : [event],
  };

  if (existingIndex < 0) {
    return [...groups, nextGroup];
  }

  const nextGroups = [...groups];
  nextGroups[existingIndex] = nextGroup;
  return nextGroups;
}
